import { useState, useEffect, useMemo } from 'react';
import Sidebar from './components/sidebar/Sidebar';
import { fetchAppConfig } from './lib/fetchMenu';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
    legendTableAtom,
    isDesktopAtom,
    isPrinterControlOpenAtom,
    menuAtom,
    menuItemsAtom,
    printAttributionsAtom,
    selectedLayersAtom,
    selectedBaseMapAtom,
    geoloniaBrokerStageAtom
} from './atoms';
import './App.css';
import ShareBox from './components/shareBox/ShareBox';
import MapContainer from './components/map/MapContainer';
import { decodeLayerList, encodeLayerList, walkCategories } from './lib/urlEncoding';
import ModalExportImage from './components/exportImage/ModalExportImage';
import { useSearchParams } from 'react-router-dom';
import PrintPopup from './components/printpopup/PrintPopup';
import HamburgerMenu from './components/hamburgerMenu/HamburgerMenu';
import ThemeMapsCreatePopup from './components/themeMaps/ThemeMapsCreateForm';
import ImgDialog from './components/imgDialog/ImgDialog';
import TOCDialog from './components/tocDialog/TocDialog';
import { useFetchFiwareData } from './hooks';
import datapngLegend from './assets/datapngLegend.json';
import { anyType, fiwareDataItemType, LegendEntry } from './types';
import { parseCsvToArray } from './types/lib/csvConverter';
import { findGyoseikaiObject } from './lib/objectHighlight';
import mapStyleConfig from './assets/mapStyleConfig.json';
import * as Sentry from "@sentry/react";

function App() {

  const setGeoloniaBrokerStage = useSetAtom(geoloniaBrokerStageAtom)
  setGeoloniaBrokerStage(import.meta.env.VITE_GEOLONIA_BROKER_STAGE === 'dev' ? 'dev': 'v1');
  // TODO: https://github.com/takamatsu-city/maps.takamatsu-fact.com/issues/89 修正時に 初期値を true に変更する
  const isDesktop = useMediaQuery({ query: '(min-width: 769px)' });
  const [isOpenedSidebar, setIsOpenedSidebar] = useState<boolean>(isDesktop);

  const [menuItem, setMenuItems] = useAtom(menuItemsAtom);
  const [menu, setMenu] = useAtom(menuAtom);
  const setIsDesktop = useSetAtom(isDesktopAtom);
  const setFiwareLegend = useSetAtom(legendTableAtom);
  const setPrintAttributions = useSetAtom(printAttributionsAtom);
  const isPrinterControlOpen = useAtomValue(isPrinterControlOpenAtom);
  const [selectedLayers, setSelectedLayers] = useAtom(selectedLayersAtom);
  const [selectedBaseMap, setSelectedBaseMap] = useAtom(selectedBaseMapAtom);

  const gyoseikai = findGyoseikaiObject(menuItem);

  // URL
  const [searchParams, setSearchParams] = useSearchParams();


  // 選択中のレイヤーを取得
  const initialSelectedLayer = useMemo(() => decodeLayerList(searchParams.get('layers')??'', menu), [menu]);
  const initialBaseMap = useMemo(() => mapStyleConfig.find(style => style.id === searchParams.get('baseMap')), []);

  // 初期値を取得
  const { initialCenter, initialZoom, initialPitch } = useMemo(() => {
    return {
      initialCenter: searchParams.get('center')?.split('/').map(v => parseFloat(v)),
      initialZoom: Number(searchParams.get('zoom')),
      initialPitch: Number(searchParams.get('pitch'))
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appConfig = useQuery({
    queryKey: ['appConfig'],
    queryFn: async () => {
      const res = await fetchAppConfig('./app.yml');
      if(initialCenter) { res.center = initialCenter; }
      if(initialZoom) { res.zoom = initialZoom; }
      if(initialPitch) { res.pitch = initialPitch; }
      return res;
    }
  });

  /* *******************
   * PC or スマホの判定
   * *******************/
  useEffect(() => {
    setIsDesktop(isDesktop);
  }, [isDesktop, setIsDesktop]);


  /* *****************
   * メニューアイテムを取得
   * *****************/
  useEffect(() => {
    if (!appConfig.data) { return; }
    setMenu(appConfig.data.menus);
    setMenuItems([...walkCategories(appConfig.data.menus)]);
  }, [appConfig.data, setMenu, setMenuItems]);


  /* ******************
  * FIWAREデータ取得
  * ******************/
  useFetchFiwareData(appConfig.data?.menus ? appConfig.data.menus : undefined);

  
  /* ************************
  * 印刷の定型文を取得
  * ************************/
  useEffect(() => {
      const fetchData = async () => {
          try {
            const attribution = await fetch('https://raw.githubusercontent.com/yaizu-city/app/main/印刷/出展.csv');
            const resAttribution = await attribution.text();
            setPrintAttributions(parseCsvToArray(resAttribution));
          } catch (error) {
            console.error('Error fetching data:', error);
          }
      };

      fetchData();
  }, []);



  /* ******************
    * 選択ずみのレイヤーをセット
    * ******************/
  useEffect(() => {
    // 初期表示のレイヤーがあったら
    if(initialSelectedLayer.length > 0) {
      setSelectedLayers(initialSelectedLayer);
      return;
    }

    setSelectedLayers(prev => {
      const newLayers = gyoseikai ? [gyoseikai.id] : [];
      return JSON.stringify(prev) === JSON.stringify(newLayers) ? prev : newLayers;
    });

  }, [initialSelectedLayer]);

  
  /* ************************
  * レイヤーの選択をURLにセット
  * ************************/
  useEffect(() => {
    if(!menu) { return; }

    setSearchParams((prev) => {
      prev.set('layers', selectedLayers ? encodeLayerList(selectedLayers, menu) : '');
      return prev;
    });
  }, [selectedLayers, menu]);


  /* ************************
  * 背景図の選択をURLにセット
  * ************************/
  useEffect(() => {
    setSearchParams((prev) => {
      if (selectedBaseMap && prev.get('baseMap') !== selectedBaseMap.id) { 
        prev.set('baseMap', selectedBaseMap.id);
      }
      return prev;
    });
  }, [selectedBaseMap]);


  /* ******************
    * URL取得の背景地図を設定
    * ******************/
  useEffect(() => {
    if(!initialBaseMap) { return; }
    setSelectedBaseMap(initialBaseMap);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBaseMap]);


  /* *****************
   * datapng用の凡例を取得
   * *****************/
  useEffect(() => {
    if (!menuItem) { return; }

    const datapngItems = Object.values(menuItem).filter(item => item.metadata?.dataType === 'datapng');

    const legends = datapngItems.reduce((acc, item) => {
      const legendType = item.metadata?.legendType as string;
      const targetKey = Object.keys(datapngLegend).find(key => key === legendType);

      if (targetKey) {
        const id = item.dataType === 'fiware' ? (item as unknown as fiwareDataItemType).dataId : item.tileId ;
        acc[id] = (datapngLegend as anyType)[targetKey];
      }
      return acc;
    }, {} as { [key: string]: LegendEntry });

    setFiwareLegend((prev) => {
      return prev ? { ...prev, ...legends } : legends;
    });

    // 必要に応じて、状態を更新するなどの処理を行う
  }, [menuItem]);


  return (
    <>
      <Sentry.ErrorBoundary fallback={<div>エラーが発生しました</div>}>
        <div className="App">

          {/* ダイアログの表示 */}
          <TOCDialog />
          {
            appConfig.data ?
              <>
                <ThemeMapsCreatePopup />
                <HamburgerMenu isDesktop={isDesktop} />
                <div className="container">
                  <Sidebar
                    siteName={appConfig.data.name}
                    isDesktop={isDesktop}
                    isOpenedSidebar={isOpenedSidebar}
                    setIsOpenedSidebar={setIsOpenedSidebar}
                  />
                  <MapContainer
                    isDesktop={isDesktop}
                    setSearchParams={setSearchParams}
                    searchParams={searchParams}
                    appSettings={appConfig.data}
                  />
                </div>
                <ShareBox />
                <ModalExportImage />
                { /* 印刷 */
                  isPrinterControlOpen && <PrintPopup />
                }
                { /* 画像ダイアログ */
                  <ImgDialog />
                }
              </>
              :
              <div>データを取得中です</div>
          }
        </div>
      </Sentry.ErrorBoundary>
    </>
  )
}

export default App;
